import { Chip, Grid, Typography } from '@mui/material';
import { parseSecondsToHM } from '@nx-smartmonkey/shared/helpers';
import { TimeWindows } from '@nx-smartmonkey/shared/interfaces';
import { RoutalPalette, RoutalSearchInput, RoutalTooltip } from '@nx-smartmonkey/ui';
import _ from 'lodash';
import { useState } from 'react';

import { Client } from '../../domain/client/Client';
import { useSearchClients } from '../../hooks/client/useSearchClients';
import { useRetrieveActiveProject } from '../../hooks/project/useRetrieveActiveProject';
import Text from '../../react-components/Text';
import { DataTable } from '../../react-components/data-table/DataTable';

type StopsFromClientsProps = {
  clientStops: Client[];
  setClientStops: (clients: Client[]) => void;
};

export const StopsFromClients = ({ clientStops: newClients, setClientStops: setNewClients }: StopsFromClientsProps) => {
  const { data: activeProject } = useRetrieveActiveProject();
  const [clientOffset, setClientOffset] = useState<number>(0);
  const [clientLimit, setClientLimit] = useState<number>(100);

  const [filterText, setFilterText] = useState<string>(``);
  const { data: clients } = useSearchClients({
    offset: clientOffset,
    limit: clientLimit,
    searchText: filterText,
  });

  const { docs, limit, page, pages, total } = clients || {};

  return (
    <Grid container direction="column" wrap="nowrap" style={{ height: `100%`, marginTop: `16px` }}>
      <Grid
        container
        item
        justifyContent="space-between"
        alignItems="center"
        style={{ height: `36px`, marginBottom: `8px` }}
      >
        <Grid item>
          {!!total && (
            <Text
              style={{ color: RoutalPalette.primary20 }}
              variant="body"
              intlId="plans.dialogs.services_from_clients.total_clients"
              intlValues={{ total }}
            />
          )}
        </Grid>
        <Grid item>
          <Grid container direction="column" justifyContent="center" style={{ height: `100%` }}>
            <RoutalSearchInput
              inputContainerStyles={{
                width: `260px`,
              }}
              bGColor={RoutalPalette.neutral02n}
              onChange={_.debounce((text: any) => {
                setFilterText(text);
              }, 300)}
              searchLive
            />
          </Grid>
        </Grid>
      </Grid>

      <div style={{ height: `calc(100% - 36px)` }}>
        <DataTable
          docs={(docs ?? []).map((client) => ({
            doc: client,
            checkboxIcon: { element: <div style={{ width: `24px` }} /> },
          }))}
          total={total ?? 0}
          limit={limit}
          page={page}
          pages={pages}
          emptyMessage={<Text intlId="no_results" />}
          onChangePage={({ offset, limit }) => {
            setClientOffset(offset);
            setClientLimit(limit);
          }}
          selectedIds={newClients.map((client) => client.id)}
          onMultipleSelection={({ selectedIds, fromGlobalCheckbox }) => {
            if (fromGlobalCheckbox) {
              if (newClients.length > 0) {
                setNewClients([]);
              } else {
                setNewClients(docs ?? []);
              }
            } else {
              const clients = docs?.filter((client) => (selectedIds ?? []).includes(client.id)) ?? [];
              clients.push(
                ...newClients.filter(
                  (selectedClient) => !(docs ?? []).map((docClient) => docClient.id).includes(selectedClient.id)
                )
              );
              setNewClients(_.uniqBy(clients, `id`));
            }
          }}
          onRowDoubleClick={(client) => {
            const selectedClient = docs?.find((clientRow) => clientRow.id === client.id);
            if (!selectedClient) return;
            if (newClients.some((client) => client.id === selectedClient.id)) {
              setNewClients(newClients.filter((client) => client.id !== selectedClient.id));
            } else {
              setNewClients(_.uniqBy([...newClients, selectedClient], `id`));
            }
          }}
          columns={[
            {
              accessorName: `label`,
              name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="clients.data.details.label" />,
              formatter: ({ value: label }) => (
                <Grid container direction="row" style={{ height: `32px` }} alignItems="center">
                  {label && label !== `` ? (
                    <RoutalTooltip title={label}>
                      <Text variant="body">{label.length > 30 ? `${label.substring(0, 30)}...` : `${label}`}</Text>
                    </RoutalTooltip>
                  ) : (
                    <Text variant="body" style={{ color: RoutalPalette.secondary00 }} intlId="no_name" />
                  )}
                </Grid>
              ),
            },
            {
              accessorName: `location`,
              name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="clients.data.details.location" />,
              formatter: ({ value: location }) =>
                location ? (
                  <RoutalTooltip title={location?.label ?? ``}>
                    <Text variant="body" style={{ maxWidth: `240px` }}>
                      {location.label}
                    </Text>
                  </RoutalTooltip>
                ) : (
                  `-`
                ),
            },
            ...(activeProject?.constraints.time_windows
              ? [
                  {
                    accessorName: `default_time_windows`,
                    name: (
                      <Text
                        style={{ color: RoutalPalette.primary20 }}
                        isBold
                        intlId="clients.data.details.default_time_windows"
                      />
                    ),
                    formatter: ({ value }: { value: TimeWindows }) =>
                      value.map((d: any) => (
                        <Chip key={d[0] + d[1]} label={`${parseSecondsToHM(d[0])}-${parseSecondsToHM(d[1])}`} />
                      )),
                  },
                ]
              : []),
            {
              accessorName: `phone`,
              name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="clients.data.details.phone" />,
            },
            {
              accessorName: `comments`,
              name: <Text style={{ color: RoutalPalette.primary20 }} isBold intlId="clients.data.details.comments" />,
              formatter: ({ value: comments }: { value: string }) => (
                <Typography style={{ maxWidth: `700px` }} noWrap>
                  {comments}
                </Typography>
              ),
            },
          ]}
        />
      </div>
    </Grid>
  );
};
